import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Flame, Plus, Minus, RefreshCw, Loader2, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const inr = (n) => `\u20b9${Number(n || 0).toLocaleString('en-IN')}`;

const AdWalletManagement = () => {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [modal, setModal] = useState(null); // { userId, name, type }
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/admin/ad-wallets`); setWallets(Array.isArray(data) ? data : []); }
    catch (e) { toast({ title: 'Failed to load', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openModal = (w, type) => { setModal({ userId: w.userId, name: w.name, type }); setAmount(''); setNote(''); };

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast({ title: 'Enter valid amount', variant: 'destructive' }); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/ad-wallet/${modal.userId}/${modal.type}`, { amount: amt, note });
      toast({ title: modal.type === 'credit' ? 'Funds added' : 'Ad spend recorded' });
      setModal(null); await load();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const shown = wallets.filter((w) => !q || (w.name || '').toLowerCase().includes(q.toLowerCase()) || String(w.clientId || '').includes(q) || (w.email || '').toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><Flame className="w-6 h-6 text-orange-500" /> Ad Wallets</h2>
          <p className="text-slate-500 text-sm">Add ad funds or record ads spent per client.</p>
        </div>
        <div className="flex gap-2">
          <div className="relative"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="pl-8 pr-3 py-2 rounded-lg border border-slate-200 text-sm outline-none" /></div>
          <Button onClick={load} variant="outline" size="sm"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div> : shown.length === 0 ? (
        <div className="text-center py-16 text-slate-400">No ad wallets yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-400 border-b border-slate-100">
              <th className="py-2 px-2">Client</th><th className="py-2 px-2">Balance</th><th className="py-2 px-2">Added</th><th className="py-2 px-2">Spent</th><th className="py-2 px-2">Actions</th>
            </tr></thead>
            <tbody>
              {shown.map((w) => (
                <tr key={w.userId} className="border-b border-slate-50">
                  <td className="py-3 px-2"><div className="font-semibold text-slate-800">{w.name || 'Unknown'} <span className="text-xs text-slate-400">#{w.clientId}</span></div><div className="text-xs text-slate-400">{w.email}</div></td>
                  <td className="py-3 px-2 font-bold text-blue-700">{inr(w.balance)}</td>
                  <td className="py-3 px-2 text-green-600">{inr(w.adsCredited)}</td>
                  <td className="py-3 px-2 text-red-500">{inr(w.adsSpent)}</td>
                  <td className="py-3 px-2"><div className="flex gap-2">
                    <Button size="sm" onClick={() => openModal(w, 'credit')} className="bg-green-600 hover:bg-green-700 text-white"><Plus className="w-4 h-4" /></Button>
                    <Button size="sm" onClick={() => openModal(w, 'spend')} className="bg-red-500 hover:bg-red-600 text-white"><Minus className="w-4 h-4" /></Button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={!!modal} onOpenChange={(v) => { if (!v) setModal(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{modal?.type === 'credit' ? 'Add Ad Funds' : 'Record Ads Spent'} — {modal?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount (\u20b9)" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none" />
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none" />
            <Button onClick={submit} disabled={busy} className={`w-full text-white rounded-xl ${modal?.type === 'credit' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-500 hover:bg-red-600'}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (modal?.type === 'credit' ? 'Add Funds' : 'Record Spend')}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdWalletManagement;
